import React, { useState } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { triggerIpActivitySearch } from '../api/client';
import { isDemoMode, DEMO_UNAVAILABLE_TITLE } from '../utils/demoMode';

// Starts an Automation runbook (a real job + cross-tenant Databricks spend), so
// it stays blocked in Demo Mode.
const DEMO = isDemoMode();

function formatDateForInput(date) {
  return date.toISOString().split('T')[0];
}

const MAX_DAYS = 90;

export default function IpActivitySearchModal({ isOpen, ipRow, onClose }) {
  const navigate = useNavigate();

  const today = new Date();
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const [startDate, setStartDate] = useState(formatDateForInput(weekAgo));
  const [endDate, setEndDate] = useState(formatDateForInput(today));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !ipRow) return null;

  const validate = () => {
    if (!startDate || !endDate) return 'Start date and end date are required.';
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'Invalid date.';
    if (end < start) return 'End date must be on or after start date.';
    const diffDays = (end - start) / (1000 * 60 * 60 * 24);
    if (diffDays > MAX_DAYS) return `Date range cannot exceed ${MAX_DAYS} days.`;
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await triggerIpActivitySearch(ipRow.ipAddress, { startDate, endDate });
      onClose();
      navigate(`/settings/known-malicious-ips/search/${encodeURIComponent(result.jobId)}`, {
        state: { ipAddress: ipRow.ipAddress, type: ipRow.type, startDate, endDate },
      });
    } catch (err) {
      setError(err.message || 'Failed to start search.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-white">Cross-Tenant Activity Search</h3>
            <p className="text-sm text-gray-400 mt-0.5 font-mono">{ipRow.ipAddress}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-300">
            Searches every tenant for activity tied to this IP and any sessions it touched
            (catches token-replay across different IPs).
          </p>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setError(null); }}
                className="input-field w-full text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setError(null); }}
                className="input-field w-full text-sm"
              />
            </div>
          </div>

          <div className="text-xs text-gray-500 bg-gray-900/50 rounded p-2">
            Max date range: {MAX_DAYS} days. No tenant filter — runs across all tenants.
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm text-red-400 bg-red-900/20 rounded p-2">
              <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-700">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={submitting || DEMO}
            title={DEMO ? DEMO_UNAVAILABLE_TITLE : undefined}
          >
            {submitting ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Starting...
              </>
            ) : (
              <>
                <MagnifyingGlassIcon className="h-4 w-4" />
                Start Search
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
